import * as React from 'react';
import dayjs from 'dayjs';
import {Alert, Box, Paper, Skeleton, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {PageHeader} from '../components/layouts/PageHeader';
import {ChartCard} from '../components/metrics/ChartCard';
import {ActivationChart, ActivePeopleChart, SummariesChart, TasksChart} from '../components/metrics/DashboardCharts';
import {DataTable} from '../components/metrics/DataTable';
import {KpiTile} from '../components/metrics/KpiTile';
import {useDashboard} from '../data/metrics';
import {apiErrorMessage, isNetworkError} from '../utilities/apiError';
import {
    activationSteps, dayLabel, describeChange, describeOnTime, eventLabel, formatNumber, lastSeenLabel,
} from '../utilities/dashboard';
import type {Dashboard} from '../contract';
import {DASHBOARD_DAYS} from '../contract/metrics';

const RANGES = DASHBOARD_DAYS.map((days) => ({days, label: `${days} days`}));

// What the dashboard shows for one answer from the server. Separate from the page so it can be looked at without one.
export function DashboardView({dashboard}: { dashboard: Dashboard }) {
    const {days, kpis, daily, activation, events, limits} = dashboard;
    const onTime = describeOnTime(dashboard.onTime);
    const steps = activationSteps(activation);
    const dayColumn = {header: 'Day', cell: (row: Dashboard['daily'][number]) => dayLabel(row.day)};

    return (
        <>
            <Typography component="h2" sx={{fontSize: 13, fontWeight: 650, mb: 1.5, color: 'text.secondary'}}>At a glance</Typography>
            <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))'}, mb: 4}}>
                <KpiTile label="New sign-ups" value={formatNumber(kpis.signUps.current)} caption={describeChange(kpis.signUps, days)}/>
                <KpiTile label="Active people" value={formatNumber(kpis.activePeople.current)} caption={describeChange(kpis.activePeople, days)}/>
                <KpiTile label="Tasks created" value={formatNumber(kpis.tasksCreated.current)} caption={describeChange(kpis.tasksCreated, days)}/>
                <KpiTile label="Tasks completed" value={formatNumber(kpis.tasksCompleted.current)} caption={describeChange(kpis.tasksCompleted, days)}/>
                <KpiTile label="Completed on time" value={onTime.value} caption={onTime.caption}/>
                <KpiTile label="Summaries played" value={formatNumber(kpis.summaries.current)} caption={describeChange(kpis.summaries, days)}/>
            </Box>

            <Typography component="h2" sx={{fontSize: 13, fontWeight: 650, mb: 1.5, color: 'text.secondary'}}>Activity & adoption</Typography>
            <Box sx={{display: 'grid', gap: 2.5, gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))'}, mb: 2.5}}>
                <ChartCard
                    title="Active people"
                    subtitle="People who did anything in the app, each day"
                    table={<DataTable label="Active people per day" rows={daily} columns={[dayColumn, {header: 'Active people', numeric: true, cell: (r) => formatNumber(r.activePeople)}]}/>}
                >
                    <ActivePeopleChart daily={daily}/>
                </ChartCard>

                <ChartCard
                    title="Tasks"
                    subtitle="Tasks created and completed, each day"
                    table={<DataTable label="Tasks per day" rows={daily} columns={[
                        dayColumn,
                        {header: 'Created', numeric: true, cell: (r) => formatNumber(r.tasksCreated)},
                        {header: 'Completed', numeric: true, cell: (r) => formatNumber(r.tasksCompleted)},
                    ]}/>}
                >
                    <TasksChart daily={daily}/>
                </ChartCard>

                <ChartCard
                    title="Spoken summaries"
                    subtitle={limits.summariesPerDay === null ? 'New summaries and replays, each day. New summaries incur a generation cost.' : `New summaries and replays, each day. Daily generation limit: ${formatNumber(limits.summariesPerDay)}.`}
                    table={<DataTable label="Spoken summaries per day" rows={daily} columns={[
                        dayColumn,
                        {header: 'Written', numeric: true, cell: (r) => formatNumber(r.summariesWritten)},
                        {header: 'Replayed', numeric: true, cell: (r) => formatNumber(r.summariesReplayed)},
                    ]}/>}
                >
                    <SummariesChart daily={daily} limit={limits.summariesPerDay}/>
                </ChartCard>

                <ChartCard
                    title="Getting started"
                    subtitle={`Of the ${formatNumber(activation.signedUp)} people who signed up in this period, how many went on to each step in it`}
                    table={<DataTable label="Getting started" rows={steps} columns={[
                        {header: 'Step', cell: (r) => r.label},
                        {header: 'People', numeric: true, cell: (r) => formatNumber(r.people)},
                        {header: 'Share of sign-ups', numeric: true, cell: (r) => (r.share === null ? '–' : `${r.share}%`)},
                    ]}/>}
                >
                    <ActivationChart steps={steps}/>
                </ChartCard>
            </Box>

            <ChartCard title="Event activity" subtitle="Event totals and the most recent activity in this period">
                <EventsTable events={events}/>
            </ChartCard>
        </>
    );
}

function EventsTable({events}: { events: Dashboard['events'] }) {
    return (
        <DataTable label="Events in this period" maxHeight={420} rows={events} columns={[
            {header: 'Event', cell: (r) => eventLabel(r.eventType)},
            {header: 'Count', numeric: true, cell: (r) => formatNumber(r.count)},
            {header: 'Last seen', numeric: true, cell: (r) => lastSeenLabel(r.lastSeen)},
        ]}/>
    );
}

// The business dashboard: what people do in the app, from the business events in Athena. Only the people the server lists can open
// it; for anyone else the server says no and this says why.
const MetricsPage: React.FC = () => {
    const [days, setDays] = React.useState<number>(30);
    const {data, error, isPending, isFetching} = useDashboard(days);

    return (
        <Box>
            <PageHeader eyebrow="Insights" title="Business dashboard" subtitle="Understand how people use Task Kiosk, from their first board to their daily routine."/>

            {/* One row of filters above everything they apply to. */}
            <Paper variant="outlined" sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3, p: 1.5, borderRadius: 2}}>
                <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, next) => next && setDays(next)} aria-label="Period shown">
                    {RANGES.map(({days: value, label}) => <ToggleButton key={value} value={value}>Last {label}</ToggleButton>)}
                </ToggleButtonGroup>
                {data && (
                    <Typography variant="caption" color="text.secondary">
                        Through {dayLabel(data.through)} · UTC days<br/>Updated {dayjs(data.generatedAt).format('h:mm A')} · Cached for up to 5 minutes
                    </Typography>
                )}
            </Paper>

            {isPending && !error && <Box role="status" aria-label="Loading the dashboard" sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2}}>{[1, 2, 3, 4].map((item) => <Skeleton key={item} variant="rounded" height={160}/>)}</Box>}

            {error && (
                <Alert severity={(error as any)?.response?.status === 403 ? 'info' : 'warning'}>
                    {isNetworkError(error) ? 'Network error. Please try again later.' : apiErrorMessage(error, "The dashboard couldn't be loaded.")}
                    {(error as any)?.response?.status === 403 && ' Ask an administrator for dashboard access.'}
                </Alert>
            )}

            {/* A new range keeps the old picture, dimmed, until it arrives: no flash and no jump. */}
            {data && (
                <Box sx={{opacity: isFetching ? 0.5 : 1, transition: 'opacity 150ms'}} aria-busy={isFetching}>
                    <DashboardView dashboard={data}/>
                </Box>
            )}
        </Box>
    );
};

export default MetricsPage;
